/** @jsx jsx */
import { jsx } from '@emotion/react';

import type { VFC } from 'react';
import { Fragment, useCallback, useEffect } from 'react';
import { FormattedHTMLMessage } from 'react-intl';
import { Link } from 'react-router';

import { simplifyQueryValue } from 'js/lib/queryUtils';
import { useLocation } from 'js/lib/useRouter';

import {
  MENTOR_TEMPLATE_ID,
  ORG_ADMIN_TEMPLATE_ID,
  PROGRAM_ADMIN_TEMPLATE_ID,
  STAFF_TEMPLATE_ID,
} from 'bundles/enterprise-admin-management/constants';
import { useThirdPartyOrganization } from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';
import { Spacer } from 'bundles/enterprise-ui/components';
import { useNotifications } from 'bundles/enterprise-ui/components/NotificationsContext';

import _t from 'i18n!nls/enterprise-admin-home';

const AdminInvitationNotification: VFC<{}> = () => {
  const { addNotification, renderNotifications } = useNotifications();
  const location = useLocation();
  const org = useThirdPartyOrganization();

  const roleId: string | undefined = simplifyQueryValue(location?.query?.roleTemplateIdParam);

  const getAdminRoleMessage = useCallback<() => $TSFixMe>(() => {
    return (
      {
        [ORG_ADMIN_TEMPLATE_ID]: (
          <FormattedHTMLMessage
            message={_t(
              'As an organization admin, you have full access to the platform and can manage learners, programs and administrators across the entire organization.'
            )}
          />
        ),
        [PROGRAM_ADMIN_TEMPLATE_ID]: (
          <FormattedHTMLMessage
            message={_t(
              'As a program admin, you can manage learners within the sphere of the programs you manage.  You can curate learning content, drive learner engagement and report on learning activity.'
            )}
          />
        ),
        [MENTOR_TEMPLATE_ID]: (
          <FormattedHTMLMessage
            message={_t(
              'As a mentor admin, you can communicate with learners, view analytics, and access learner results in the Gradebook.'
            )}
          />
        ),
        [STAFF_TEMPLATE_ID]: (
          <FormattedHTMLMessage
            message={_t(
              'As a {adminRole} admin, you can manage specific programs but cannot access areas of the platform that expose learners’ Personally Identifiable information (PII).'
            )}
          />
        ),
      }[String(roleId)] ?? <Fragment />
    );
  }, [roleId]);

  useEffect(() => {
    if (roleId) {
      addNotification({
        title: _t('You’ve been granted admin privileges'),
        severity: 'information',
        children: getAdminRoleMessage(),
        inlineAction: (
          <Link to={`/o/${org.slug}/new-admin/permissions?source=home`}>{_t('View all your permissions here')}</Link>
        ),
        namespace: 'AdminRoleInvitation',
        component: 'PageNotification',
      });
    }
  }, [getAdminRoleMessage, roleId, org.slug, addNotification]);

  return roleId ? (
    <Fragment>
      <Spacer size={32} />
      {renderNotifications('AdminRoleInvitation')}
    </Fragment>
  ) : null;
};

export default AdminInvitationNotification;
